import React from "react";
import { Typography } from "@mui/material";

interface ProgressMessageProps {
  children: React.ReactNode;
  progressUpdate?: boolean;
  variant?: "body1" | "body2";
  sx?: object;
}

/**
 * Component for displaying messages with consistent formatting that matches PromptPanel.
 * When progressUpdate is true, uses the same styling as progress_update messages from AssistantResponseStream:
 * - Italic font style
 * - Reduced opacity (0.6)
 * - Animated dots (...) that cycle through visibility states
 * When progressUpdate is false, uses normal styling:
 * - Normal font style
 * - Full opacity (1)
 * - No animated dots
 * Both cases include responsive font sizes matching PromptPanel
 */
const ProgressMessage: React.FC<ProgressMessageProps> = ({
  children,
  progressUpdate = false,
  variant = "body2",
  sx = {},
}) => {
  return (
    <Typography
      component="span"
      variant={variant}
      sx={{
        display: progressUpdate ? "inline-flex" : "inline",
        alignItems: progressUpdate ? "baseline" : undefined,
        whiteSpace: progressUpdate ? "nowrap" : undefined,
        fontStyle: progressUpdate ? "italic" : "normal",
        opacity: progressUpdate ? 0.6 : 1,
        fontSize: {
          xs: "0.8rem",
          sm: "0.8rem",
          md: "0.9rem",
          lg: "1rem",
          xl: "1rem",
        },
        ...(progressUpdate && {
          "&::after": {
            content: '"..."',
            "@keyframes dots": {
              "0%, 20%": {
                color: "rgba(0,0,0,0)",
                textShadow: ".25em 0 0 rgba(0,0,0,0), .5em 0 0 rgba(0,0,0,0)",
              },
              "40%": {
                color: "currentColor",
                textShadow: ".25em 0 0 rgba(0,0,0,0), .5em 0 0 rgba(0,0,0,0)",
              },
              "60%": {
                textShadow: ".25em 0 0 currentColor, .5em 0 0 rgba(0,0,0,0)",
              },
              "80%, 100%": {
                textShadow: ".25em 0 0 currentColor, .5em 0 0 currentColor",
              },
            },
            display: "inline-block",
            animation: "dots 1.4s infinite",
            width: "1em",
            textAlign: "left",
            marginLeft: "0.1em",
          },
        }),
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
};

export default ProgressMessage;
